import React, { useState, useEffect, useRef } from "react";
import { Lock, KeyRound, Calendar, Users, Gift, LogOut, ChevronLeft, ChevronRight, Check, X, Trash2, Image as ImageIcon, Upload, MessageSquare, Euro, Search } from "lucide-react";
import { GALLERY_CATEGORIES } from "../mock";
import {
  adminLogin, adminVerify, changeAdminPassword, clearToken,
  listBookings, updateBookingStatus, deleteBooking,
  listGiftCards, updateGiftCard, updateGiftCardStatus, deleteGiftCard,
  getBlockedDates, addBlockedDate, addBlockedDatesBulk, removeBlockedDate,
  getBlockedSlots, addBlockedSlot, removeBlockedSlot,
  listPhotos, uploadPhoto, deletePhoto, photoUrl,
  listMessages, deleteMessage,
  listGalleryCategories, createGalleryCategory, deleteGalleryCategory
} from "../api";
import { useToast } from "../hooks/use-toast";

const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const dayNames = ["L", "M", "M", "J", "V", "S", "D"];
// JS getDay(): 0=Sun, 1=Mon, ..., 6=Sat — used for "Tous les lundis/mardis..." buttons
const dayNamesFull = ["dimanches", "lundis", "mardis", "mercredis", "jeudis", "vendredis", "samedis"];

export default function Admin() {
  const [auth, setAuth] = useState(false);
  const [pwd, setPwd] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("calendar");
  const [showPwdModal, setShowPwdModal] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    adminVerify().then(ok => setAuth(ok));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      await adminLogin(pwd);
      setAuth(true);
      toast({ title: "Connexion réussie" });
    } catch (err) {
      toast({ title: "Mot de passe incorrect", variant: "destructive" });
    }
    setLoggingIn(false);
  };

  const logout = () => {
    clearToken();
    setAuth(false);
  };

  if (!auth) {
    return (
      <div className="bg-black text-white min-h-screen pt-32 pb-24 flex items-center justify-center px-6">
        <form onSubmit={login} className="max-w-sm w-full border border-gold/40 bg-zinc-950/60 p-8">
          <Lock size={36} className="text-gold mx-auto mb-4" strokeWidth={1} />
          <h1 className="text-2xl text-center mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Espace Admin
          </h1>
          <p className="text-gray-400 text-xs text-center mb-6 uppercase tracking-wider">Connexion Ludovic</p>
          <input
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="Mot de passe"
            className="w-full bg-black border border-white/20 focus:border-gold focus:outline-none p-3 text-white mb-4"
          />
          <button type="submit" disabled={loggingIn} className="btn-gold-filled w-full justify-center disabled:opacity-50">
            {loggingIn ? "..." : "Se connecter"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-gold text-xs uppercase tracking-widest mb-2">Administration</p>
            <h1 className="text-4xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Tableau de bord
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPwdModal(true)}
              className="btn-gold text-xs"
              data-testid="admin-change-pwd-btn"
              title="Changer mon mot de passe"
            >
              <KeyRound size={14} />
              <span className="hidden sm:inline">Mot de passe</span>
            </button>
            <button onClick={logout} className="btn-gold text-xs" data-testid="admin-logout-btn">
              <LogOut size={14} />
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-8 overflow-x-auto">
          <TabBtn active={tab === "calendar"} onClick={() => setTab("calendar")} Icon={Calendar}>Calendrier</TabBtn>
          <TabBtn active={tab === "bookings"} onClick={() => setTab("bookings")} Icon={Users}>Réservations</TabBtn>
          <TabBtn active={tab === "gifts"} onClick={() => setTab("gifts")} Icon={Gift}>Cartes cadeaux</TabBtn>
          <TabBtn active={tab === "photos"} onClick={() => setTab("photos")} Icon={ImageIcon}>Photos galerie</TabBtn>
          <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} Icon={MessageSquare}>Messages</TabBtn>
        </div>

        {tab === "calendar" && <AdminCalendar />}
        {tab === "bookings" && <AdminBookings />}
        {tab === "gifts" && <AdminGifts />}
        {tab === "photos" && <AdminPhotos />}
        {tab === "messages" && <AdminMessages />}

        {showPwdModal && <ChangePasswordModal onClose={() => setShowPwdModal(false)} />}
      </div>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const { toast } = useToast();
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) {
      toast({ title: "Trop court", description: "Le nouveau mot de passe doit contenir au moins 8 caractères.", variant: "destructive" });
      return;
    }
    if (newPwd !== confirmPwd) {
      toast({ title: "Confirmation incorrecte", description: "Les deux nouveaux mots de passe ne correspondent pas.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await changeAdminPassword(oldPwd, newPwd);
      toast({ title: "✅ Mot de passe modifié", description: "Utilisez le nouveau mot de passe à la prochaine connexion." });
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Échec";
      toast({ title: "Erreur", description: msg, variant: "destructive" });
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="change-pwd-modal">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-zinc-950 border border-gold/40 p-6 max-w-md w-full space-y-4"
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-gold text-lg" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Changer mon mot de passe
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <input
          type="password"
          required
          placeholder="Mot de passe actuel"
          autoComplete="current-password"
          value={oldPwd}
          onChange={(e) => setOldPwd(e.target.value)}
          className="w-full bg-black border border-white/20 focus:border-gold focus:outline-none p-3 text-white"
          data-testid="change-pwd-old"
        />
        <input
          type="password"
          required
          placeholder="Nouveau mot de passe (8 caractères min)"
          autoComplete="new-password"
          value={newPwd}
          onChange={(e) => setNewPwd(e.target.value)}
          className="w-full bg-black border border-white/20 focus:border-gold focus:outline-none p-3 text-white"
          data-testid="change-pwd-new"
        />
        <input
          type="password"
          required
          placeholder="Confirmer le nouveau mot de passe"
          autoComplete="new-password"
          value={confirmPwd}
          onChange={(e) => setConfirmPwd(e.target.value)}
          className="w-full bg-black border border-white/20 focus:border-gold focus:outline-none p-3 text-white"
          data-testid="change-pwd-confirm"
        />
        <p className="text-xs text-gray-500">
          ⚠️ Une fois modifié, l'ancien mot de passe ne fonctionnera plus. Choisissez-en un que vous retiendrez.
        </p>
        <button
          type="submit"
          disabled={submitting}
          className="btn-gold-filled w-full justify-center disabled:opacity-50"
          data-testid="change-pwd-submit"
        >
          {submitting ? "Enregistrement..." : "Enregistrer le nouveau mot de passe"}
        </button>
      </form>
    </div>
  );
}

function TabBtn({ active, onClick, Icon, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-wider border transition-colors whitespace-nowrap ${
        active ? "border-gold bg-gold text-black" : "border-white/20 text-white/70 hover:border-gold"
      }`}
    >
      <Icon size={16} />
      {children}
    </button>
  );
}

function AdminCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [blocked, setBlocked] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [timeRange, setTimeRange] = useState({ start: "", end: "" });
  const { toast } = useToast();

  // Full possible range (6h–22h). The reservation page filters per-date,
  // but the admin can pre-block any potential slot.
  const TIME_SLOTS = Array.from({ length: 17 }, (_, i) => `${String(i + 6).padStart(2, '0')}:00`);

  const refreshAll = async () => {
    const [bl, res] = await Promise.all([
      getBlockedDates(),
      getBlockedSlots()
    ]);
    setBlocked(bl);
    setBlockedSlots(res);
  };
  useEffect(() => { refreshAll(); }, []);

  const month = currentDate.getMonth();
  const year = currentDate.getFullYear();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = lastDay.getDate();

  const days = [];
  for (let i = 0; i < startOffset; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));

  const toggleFullDay = async () => {
    if (!selectedDay) return;
    const key = selectedDay.toISOString().split('T')[0];
    if (blocked.includes(key)) {
      await removeBlockedDate(key);
      setBlocked(blocked.filter(k => k !== key));
      toast({ title: "✅ Journée débloquée" });
    } else {
      await addBlockedDate(key);
      setBlocked([...blocked, key]);
      toast({ title: "🔒 Journée entièrement bloquée" });
    }
  };

  const toggleSlot = async (time) => {
    if (!selectedDay) return;
    const key = selectedDay.toISOString().split('T')[0];
    const isBlocked = blockedSlots.some(s => s.date === key && s.time === time);
    if (isBlocked) {
      await removeBlockedSlot(key, time);
    } else {
      await addBlockedSlot(key, time);
    }
    await refreshAll();
  };

  const blockTimeRange = async () => {
    if (!selectedDay || !timeRange.start || !timeRange.end) return;
    const key = selectedDay.toISOString().split('T')[0];
    const startH = parseInt(timeRange.start.split(':')[0]);
    const endH = parseInt(timeRange.end.split(':')[0]);
    if (startH > endH) { toast({ title: "Horaires invalides", variant: "destructive" }); return; }
    const slotsToAdd = TIME_SLOTS.filter(s => {
      const h = parseInt(s.split(':')[0]);
      return h >= startH && h <= endH;
    });
    for (const t of slotsToAdd) {
      const already = blockedSlots.some(s => s.date === key && s.time === t);
      if (!already) await addBlockedSlot(key, t);
    }
    await refreshAll();
    toast({ title: `🔒 ${slotsToAdd.length} créneaux bloqués (${timeRange.start} → ${timeRange.end})` });
    setTimeRange({ start: "", end: "" });
  };

  const blockWeekdays = async () => {
    const toAdd = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const day = date.getDay();
      if (day >= 1 && day <= 5) {
        const key = date.toISOString().split('T')[0];
        if (!blocked.includes(key)) toAdd.push(key);
      }
    }
    if (toAdd.length > 0) {
      await addBlockedDatesBulk(toAdd);
      setBlocked([...blocked, ...toAdd]);
      toast({ title: `🔒 ${toAdd.length} jours bloqués (lun-ven)` });
    }
  };

  const blockWeekends = async () => {
    const toAdd = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const day = date.getDay();
      if (day === 0 || day === 6) {
        const key = date.toISOString().split('T')[0];
        if (!blocked.includes(key)) toAdd.push(key);
      }
    }
    if (toAdd.length > 0) {
      await addBlockedDatesBulk(toAdd);
      setBlocked([...blocked, ...toAdd]);
      toast({ title: `🔒 ${toAdd.length} jours bloqués (sam-dim)` });
    }
  };

  const blockSpecificWeekday = async (weekday) => {
    const toAdd = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      if (date.getDay() === weekday) {
        const key = date.toISOString().split('T')[0];
        if (!blocked.includes(key)) toAdd.push(key);
      }
    }
    if (toAdd.length > 0) {
      await addBlockedDatesBulk(toAdd);
      setBlocked([...blocked, ...toAdd]);
      toast({ title: `🔒 ${toAdd.length} "${dayNamesFull[weekday]}" bloqués` });
    } else {
      toast({ title: "Tous déjà bloqués" });
    }
  };

  const clearMonth = async () => {
    if (!window.confirm('Débloquer tous les jours de ce mois ?')) return;
    const toRemove = blocked.filter(k => {
      const d = new Date(k);
      return d.getMonth() === month && d.getFullYear() === year;
    });
    for (const k of toRemove) await removeBlockedDate(k);
    setBlocked(blocked.filter(k => !toRemove.includes(k)));
    toast({ title: `✅ ${toRemove.length} jours débloqués` });
  };

  const selectedDayKey = selectedDay ? selectedDay.toISOString().split('T')[0] : null;
  const dayFullyBlocked = selectedDayKey && blocked.includes(selectedDayKey);

  return (
    <div className="space-y-6">
      {/* Quick actions */}
      <div className="border border-gold/30 bg-gradient-to-br from-zinc-950 to-black p-5">
        <p className="text-gold text-xs uppercase tracking-wider mb-3">Actions rapides sur le mois</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={blockWeekdays} data-testid="block-weekdays-btn" className="btn-gold text-xs">Bloquer lun-ven</button>
          <button onClick={blockWeekends} data-testid="block-weekends-btn" className="btn-gold text-xs">Bloquer sam-dim</button>
          {[1,2,3,4,5,6,0].map(wd => (
            <button key={wd} onClick={() => blockSpecificWeekday(wd)} className="btn-gold text-xs">
              Tous les {dayNamesFull[wd]}
            </button>
          ))}
          <button onClick={clearMonth} data-testid="clear-month-btn" className="px-4 py-2 text-xs uppercase tracking-wider border border-red-500/40 text-red-400 hover:bg-red-500/10">
            Débloquer tout le mois
          </button>
        </div>
      </div>

      {/* Calendar */}
      <div className="border border-white/10 bg-zinc-950/40 p-6 md:p-8">
        <p className="text-gray-400 text-sm mb-4">
          👉 <strong className="text-white">Cliquez sur un jour</strong> pour gérer ses créneaux ou bloquer toute la journée
        </p>

        <div className="flex items-center justify-between mb-6">
          <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))} className="p-2 hover:text-gold" data-testid="prev-month-btn">
            <ChevronLeft size={20} />
          </button>
          <h3 className="text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {monthNames[month]} <span className="text-gold">{year}</span>
          </h3>
          <button onClick={() => setCurrentDate(new Date(year, month + 1, 1))} className="p-2 hover:text-gold" data-testid="next-month-btn">
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-2 mb-2">
          {dayNames.map((d, i) => (
            <div key={`dn-${i}`} className="text-center text-gold text-xs tracking-wider uppercase py-2">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {days.map((date, i) => {
            if (!date) return <div key={`empty-${i}`} />;
            const key = date.toISOString().split('T')[0];
            const fullyBlocked = blocked.includes(key);
            const hasSlotBlocks = blockedSlots.some(s => s.date === key);
            const isPast = date < new Date(new Date().setHours(0,0,0,0));
            const isSelected = selectedDayKey === key;
            return (
              <button
                key={key}
                onClick={() => {
                  if (isPast) return;
                  setSelectedDay(date);
                  // Scroll to the slot manager on mobile
                  setTimeout(() => {
                    document.getElementById('slot-manager')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }}
                data-testid={`admin-day-${key}`}
                disabled={isPast}
                className={`calendar-day relative ${(isPast || fullyBlocked) ? "blocked" : "available"} ${isSelected ? "!ring-2 !ring-gold" : ""}`}
              >
                {date.getDate()}
                {hasSlotBlocks && !fullyBlocked && <span className="absolute bottom-0.5 right-0.5 text-[9px] text-gold">•</span>}
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex flex-wrap gap-4 text-xs text-gray-400">
          <div className="flex items-center gap-2"><span className="w-3 h-3 bg-gold/20 border border-gold/40" />Libre</div>
          <div className="flex items-center gap-2"><span className="w-3 h-3 bg-zinc-800 border border-zinc-700" />Jour bloqué</div>
          <div className="flex items-center gap-2"><span className="w-3 h-3 bg-gold/20 border border-gold/40 relative"><span className="absolute -bottom-0.5 -right-0.5 text-[8px] text-gold">•</span></span>Créneaux partiels</div>
        </div>
      </div>

      {/* Slot manager */}
      <div id="slot-manager" className="border border-gold/30 bg-zinc-950/40 p-6 md:p-8">
        <h3 className="text-xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          Gérer le jour sélectionné
        </h3>

        {!selectedDay ? (
          <p className="text-gray-500 italic text-sm">👆 Cliquez sur un jour dans le calendrier ci-dessus.</p>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <p className="text-white capitalize">
                <strong className="text-gold">{selectedDay.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</strong>
              </p>
              <div className="flex gap-2">
                <button
                  onClick={toggleFullDay}
                  data-testid="toggle-full-day-btn"
                  className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${
                    dayFullyBlocked
                      ? "border-green-500/60 text-green-400 hover:bg-green-500/20"
                      : "border-red-500/60 text-red-400 hover:bg-red-500/20"
                  }`}
                >
                  {dayFullyBlocked ? "🔓 Débloquer la journée" : "🔒 Bloquer toute la journée"}
                </button>
                <button onClick={() => setSelectedDay(null)} className="px-3 py-2 text-xs text-gray-400 hover:text-white">
                  Fermer
                </button>
              </div>
            </div>

            {dayFullyBlocked && (
              <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 text-xs">
                ⚠️ Cette journée est entièrement bloquée. Les créneaux individuels sont ignorés tant que ce blocage complet est actif.
              </div>
            )}

            {/* Quick time range block */}
            <div className="mb-5 p-4 bg-black/40 border border-white/10">
              <p className="text-gold text-xs uppercase tracking-wider mb-2">Bloquer une plage horaire</p>
              <div className="flex flex-wrap gap-2 items-center">
                <select
                  value={timeRange.start}
                  onChange={(e) => setTimeRange({...timeRange, start: e.target.value})}
                  data-testid="time-range-start"
                  className="bg-black border border-white/20 focus:border-gold focus:outline-none p-2 text-white text-sm"
                >
                  <option value="">De...</option>
                  {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <span className="text-gray-400 text-sm">→</span>
                <select
                  value={timeRange.end}
                  onChange={(e) => setTimeRange({...timeRange, end: e.target.value})}
                  data-testid="time-range-end"
                  className="bg-black border border-white/20 focus:border-gold focus:outline-none p-2 text-white text-sm"
                >
                  <option value="">À...</option>
                  {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <button
                  onClick={blockTimeRange}
                  disabled={!timeRange.start || !timeRange.end}
                  data-testid="block-range-btn"
                  className="btn-gold text-xs disabled:opacity-50"
                >
                  Bloquer la plage
                </button>
              </div>
              <p className="text-gray-500 text-xs mt-2 italic">Ex: 12:00 → 14:00 bloque la pause déjeuner (3 créneaux)</p>
            </div>

            {/* Individual slots */}
            <p className="text-xs uppercase tracking-wider text-gray-400 mb-3">Ou cliquez pour bloquer / débloquer chaque créneau :</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
              {TIME_SLOTS.map(slot => {
                const isSlotBlocked = blockedSlots.some(s => s.date === selectedDayKey && s.time === slot);
                return (
                  <button
                    key={slot}
                    onClick={() => toggleSlot(slot)}
                    data-testid={`slot-${slot}`}
                    className={`py-3 text-sm tracking-wider border transition-all ${
                      isSlotBlocked
                        ? "border-red-500/50 bg-red-500/20 text-red-300 line-through"
                        : "border-gold/40 text-gold hover:bg-gold hover:text-black"
                    }`}
                  >
                    {slot}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { toast } = useToast();

  const refresh = () => listBookings().then(setBookings).finally(() => setLoading(false));

  useEffect(() => { refresh(); }, []);

  const updateStatus = async (id, status) => {
    await updateBookingStatus(id, status);
    refresh();
    const labels = {
      pending_payment: "passée en attente de paiement",
      "confirmé": "confirmée ✅ (acompte reçu, créneau bloqué)",
      "soldée": "soldée 💰 (solde + trajet encaissés)",
      "refusé": "refusée"
    };
    toast({ title: `Réservation ${labels[status] || status}` });
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer cette réservation ?')) return;
    await deleteBooking(id);
    refresh();
    toast({ title: "Supprimée" });
  };

  // Filter bookings by search text + status
  const filteredBookings = bookings.filter(b => {
    if (statusFilter !== "all" && b.status !== statusFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (b.name || "").toLowerCase().includes(q) ||
      (b.email || "").toLowerCase().includes(q) ||
      (b.phone || "").toLowerCase().includes(q) ||
      (b.ref || "").toLowerCase().includes(q) ||
      (b.city || "").toLowerCase().includes(q) ||
      (b.address || "").toLowerCase().includes(q) ||
      (b.prestationTitle || "").toLowerCase().includes(q) ||
      (b.giftCode || "").toLowerCase().includes(q) ||
      (b.dateFormatted || "").toLowerCase().includes(q)
    );
  });

  const statusCounts = {
    all: bookings.length,
    pending_payment: bookings.filter(b => b.status === "pending_payment").length,
    "confirmé": bookings.filter(b => b.status === "confirmé").length,
    "soldée": bookings.filter(b => b.status === "soldée").length,
    "refusé": bookings.filter(b => b.status === "refusé").length,
  };

  if (loading) return <div className="p-10 text-center text-gray-400">Chargement...</div>;

  return (
    <div className="space-y-4">
      {/* Search + filters */}
      <div className="border border-gold/30 bg-zinc-950/40 p-4 space-y-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par nom, email, tél, ville, ref, code cadeau..."
            data-testid="bookings-search"
            className="w-full bg-black border border-white/20 focus:border-gold focus:outline-none pl-10 pr-10 py-2.5 text-white text-sm"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
              <X size={16} />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { v: "all", l: "Toutes" },
            { v: "pending_payment", l: "⏳ Attente" },
            { v: "confirmé", l: "🔵 Confirmées" },
            { v: "soldée", l: "✅ Soldées" },
            { v: "refusé", l: "❌ Refusées" },
          ].map(s => (
            <button
              key={s.v}
              onClick={() => setStatusFilter(s.v)}
              data-testid={`filter-status-${s.v}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
                statusFilter === s.v ? "border-gold bg-gold text-black" : "border-white/20 text-white/70 hover:border-gold"
              }`}
            >
              {s.l} ({statusCounts[s.v]})
            </button>
          ))}
        </div>
      </div>

      {filteredBookings.length === 0 ? (
        <div className="border border-white/10 bg-zinc-950/40 p-10 text-center">
          <p className="text-gray-400">
            {bookings.length === 0 ? "Aucune réservation pour le moment." : "Aucune réservation ne correspond à votre recherche."}
          </p>
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-500">{filteredBookings.length} résultat{filteredBookings.length > 1 ? 's' : ''}</p>
          {filteredBookings.map(b => (
        <div key={b.id} className="border border-white/10 bg-zinc-950/40 p-5">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-gold font-mono text-xs">#{b.ref}</span>
                <StatusBadge status={b.status} />
              </div>
              <p className="text-xl mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                {b.prestationTitle} — <span className="text-gold">{b.amount}€</span>
              </p>
              <p className="text-gray-300 text-sm capitalize mb-1">{b.dateFormatted}</p>
              <p className="text-gray-400 text-sm">
                {b.name} • <a href={`mailto:${b.email}`} className="hover:text-gold">{b.email}</a> • <a href={`tel:${b.phone}`} className="hover:text-gold">{b.phone}</a>
              </p>
              {b.location && <p className="text-gray-400 text-sm">📍 {b.location}</p>}
              {b.city && (
                <p className="text-gray-400 text-sm">🏠 {b.address ? b.address + ', ' : ''}{b.city}
                  {typeof b.travel_km === 'number' && (
                    <span className="text-gold ml-2">• {b.travel_km} km → {b.travel_fee}€ trajet</span>
                  )}
                </p>
              )}
              {b.preferredTime && <p className="text-gray-400 text-sm">⏰ {b.preferredTime}</p>}
              {b.message && <p className="text-gray-500 text-xs italic mt-2">"{b.message}"</p>}
              {b.giftCode && <p className="text-gold text-xs mt-1">🎁 Code cadeau : {b.giftCode}</p>}
              {b.paid_by_gift_card && <p className="text-green-400 text-xs mt-1">✅ Prestation réglée par carte cadeau</p>}
              <p className="text-gray-500 text-xs mt-2">Paiement : {b.payment.toUpperCase()}</p>

              {/* Payment tracking summary */}
              {b.status === 'pending_payment' && (
                <div className="mt-2 text-xs bg-orange-500/10 border border-orange-500/30 p-2 text-orange-300">
                  ⏳ Attente acompte : <strong>{b.deposit}€</strong>
                </div>
              )}
              {b.status === 'confirmé' && !b.paid_by_gift_card && (
                <div className="mt-2 text-xs bg-blue-500/10 border border-blue-500/30 p-2 text-blue-300">
                  ✅ Acompte {b.deposit}€ reçu • <strong>Reste à encaisser le jour J : {(b.amount - b.deposit) + (b.travel_fee || 0)}€</strong>
                  {b.travel_fee ? ` (dont ${b.travel_fee}€ trajet)` : ''}
                </div>
              )}
              {b.status === 'confirmé' && b.paid_by_gift_card && b.travel_fee > 0 && (
                <div className="mt-2 text-xs bg-blue-500/10 border border-blue-500/30 p-2 text-blue-300">
                  🎁 Prestation payée • <strong>Reste à encaisser le jour J : {b.travel_fee}€ (trajet)</strong>
                </div>
              )}
              {b.status === 'soldée' && (
                <div className="mt-2 text-xs bg-green-500/10 border border-green-500/30 p-2 text-green-300">
                  ✅ Totalement soldée — {(b.amount || 0) + (b.travel_fee || 0)}€ encaissés
                </div>
              )}
            </div>
            <div className="flex gap-2">
              {b.status === 'pending_payment' && (
                <button
                  onClick={() => updateStatus(b.id, 'confirmé')}
                  data-testid={`mark-paid-${b.id}`}
                  className="p-2 px-3 border border-blue-500/60 hover:bg-blue-500 hover:text-white text-blue-400 flex items-center gap-1 text-xs uppercase tracking-wider"
                  title="Acompte reçu — confirmer la réservation (le créneau sera bloqué)"
                >
                  <Euro size={14} /> Acompte reçu
                </button>
              )}
              {b.status === 'confirmé' && (
                <button
                  onClick={() => updateStatus(b.id, 'soldée')}
                  data-testid={`mark-settled-${b.id}`}
                  className="p-2 px-3 border border-green-500/60 hover:bg-green-500 hover:text-white text-green-400 flex items-center gap-1 text-xs uppercase tracking-wider"
                  title={`Solde + trajet encaissés le jour J (total ${(b.amount || 0) + (b.travel_fee || 0)}€)`}
                >
                  <Euro size={14} /> Soldée
                </button>
              )}
              {b.status !== 'confirmé' && b.status !== 'pending_payment' && b.status !== 'soldée' && (
                <button onClick={() => updateStatus(b.id, 'confirmé')} className="p-2 border border-gold/40 hover:bg-gold hover:text-black text-gold" title="Confirmer">
                  <Check size={16} />
                </button>
              )}
              {b.status !== 'refusé' && b.status !== 'soldée' && (
                <button onClick={() => updateStatus(b.id, 'refusé')} className="p-2 border border-red-500/40 hover:bg-red-500 hover:text-white text-red-400" title="Refuser">
                  <X size={16} />
                </button>
              )}
              <button onClick={() => del(b.id)} className="p-2 border border-white/20 hover:bg-white/10 text-gray-400" title="Supprimer">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>
      ))}
        </>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    pending_payment: { bg: "bg-orange-500/20", text: "text-orange-400", label: "Attente paiement" },
    en_attente: { bg: "bg-yellow-500/20", text: "text-yellow-400", label: "En attente" },
    'confirmé': { bg: "bg-blue-500/20", text: "text-blue-400", label: "Confirmée" },
    'soldée': { bg: "bg-green-500/20", text: "text-green-400", label: "Soldée ✓" },
    'refusé': { bg: "bg-red-500/20", text: "text-red-400", label: "Refusée" }
  };
  const c = cfg[status] || cfg.pending_payment;
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${c.bg} ${c.text}`}>{c.label}</span>;
}

function AdminGifts() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { toast } = useToast();

  const refresh = () => listGiftCards().then(setCards).finally(() => setLoading(false));
  useEffect(() => { refresh(); }, []);

  const toggleUsed = async (id, currentUsed) => {
    await updateGiftCard(id, !currentUsed);
    refresh();
    toast({ title: "Statut mis à jour" });
  };

  const markPaid = async (id, recipientName) => {
    if (!window.confirm(`Confirmer la réception du paiement ?\n\nLe code sera envoyé par email à ${recipientName}.`)) return;
    await updateGiftCardStatus(id, 'active');
    refresh();
    toast({ title: "Carte activée ✅", description: `Le code a été envoyé à ${recipientName}.` });
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer cette carte cadeau ?')) return;
    await deleteGiftCard(id);
    refresh();
  };

  const filteredCards = cards.filter(c => {
    if (statusFilter === "pending" && c.status !== "pending_payment") return false;
    if (statusFilter === "active" && (c.status === "pending_payment" || c.used)) return false;
    if (statusFilter === "used" && !c.used) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (c.code || "").toLowerCase().includes(q) ||
      (c.buyerName || "").toLowerCase().includes(q) ||
      (c.buyerEmail || "").toLowerCase().includes(q) ||
      (c.recipientName || "").toLowerCase().includes(q) ||
      (c.recipientEmail || "").toLowerCase().includes(q) ||
      (c.prestationTitle || "").toLowerCase().includes(q) ||
      String(c.amount || "").includes(q)
    );
  });

  const counts = {
    all: cards.length,
    pending: cards.filter(c => c.status === "pending_payment").length,
    active: cards.filter(c => c.status !== "pending_payment" && !c.used).length,
    used: cards.filter(c => c.used).length,
  };

  if (loading) return <div className="p-10 text-center text-gray-400">Chargement...</div>;

  return (
    <div className="space-y-4">
      {/* Search + filters */}
      <div className="border border-gold/30 bg-zinc-950/40 p-4 space-y-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par code, nom acheteur ou bénéficiaire, email, montant..."
            data-testid="gifts-search"
            className="w-full bg-black border border-white/20 focus:border-gold focus:outline-none pl-10 pr-10 py-2.5 text-white text-sm"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
              <X size={16} />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { v: "all", l: "Toutes" },
            { v: "pending", l: "⏳ Attente paiement" },
            { v: "active", l: "🟢 Actives" },
            { v: "used", l: "⚪ Utilisées" },
          ].map(s => (
            <button
              key={s.v}
              onClick={() => setStatusFilter(s.v)}
              data-testid={`gift-filter-${s.v}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
                statusFilter === s.v ? "border-gold bg-gold text-black" : "border-white/20 text-white/70 hover:border-gold"
              }`}
            >
              {s.l} ({counts[s.v]})
            </button>
          ))}
        </div>
      </div>

      {filteredCards.length === 0 ? (
        <div className="border border-white/10 bg-zinc-950/40 p-10 text-center">
          <p className="text-gray-400">
            {cards.length === 0 ? "Aucune carte cadeau émise." : "Aucune carte ne correspond à votre recherche."}
          </p>
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-500">{filteredCards.length} résultat{filteredCards.length > 1 ? 's' : ''}</p>
          {filteredCards.map(c => {
        const isPending = c.status === 'pending_payment';
        return (
        <div key={c.id} className="border border-white/10 bg-zinc-950/40 p-5">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="text-gold font-mono">{c.code}</span>
                {(() => {
                  if (isPending) {
                    return <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-orange-500/20 text-orange-400">Attente paiement</span>;
                  }
                  if (c.used) {
                    return <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-gray-500/20 text-gray-400">Utilisée</span>;
                  }
                  return <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-green-500/20 text-green-400">Active</span>;
                })()}
              </div>
              <p className="text-xl mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                {c.prestationTitle} — <span className="text-gold">{c.amount}€</span>
              </p>
              <p className="text-gray-400 text-sm">Acheteur : {c.buyerName} ({c.buyerEmail})</p>
              <p className="text-gray-400 text-sm">Bénéficiaire : {c.recipientName} ({c.recipientEmail})</p>
              {c.message && <p className="text-gray-500 text-xs italic mt-2">"{c.message}"</p>}
              <p className="text-gray-500 text-xs mt-2">Paiement : {c.payment.toUpperCase()}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {isPending && (
                <button
                  onClick={() => markPaid(c.id, c.recipientName)}
                  data-testid={`gift-mark-paid-${c.id}`}
                  className="p-2 px-3 border border-green-500/60 hover:bg-green-500 hover:text-white text-green-400 flex items-center gap-1 text-xs uppercase tracking-wider"
                  title="Paiement reçu — activer la carte et envoyer le code au bénéficiaire"
                >
                  <Euro size={14} /> Marquer payée
                </button>
              )}
              {!isPending && (
                <button onClick={() => toggleUsed(c.id, c.used)} className="btn-gold text-xs">
                  {c.used ? "Marquer active" : "Marquer utilisée"}
                </button>
              )}
              <button onClick={() => del(c.id)} className="p-2 border border-white/20 hover:bg-white/10 text-gray-400">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>
        );
      })}
        </>
      )}
    </div>
  );
}

function AdminPhotos() {
  const [photos, setPhotos] = useState([]);
  const [customCats, setCustomCats] = useState([]);
  const [category, setCategory] = useState("mariage");
  const [filter, setFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [creatingCat, setCreatingCat] = useState(false);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const refresh = () => {
    listPhotos().then(setPhotos);
    listGalleryCategories().then(setCustomCats);
  };
  useEffect(() => { refresh(); }, []);

  const [migrating, setMigrating] = useState(false);
  const runMigration = async () => {
    if (!window.confirm("Restaurer toutes les photos legacy depuis Cloudinary ?\n\nCette opération rattache les photos déjà uploadées sur Cloudinary aux URLs de la base. Sans risque — les photos déjà à jour sont ignorées.")) return;
    setMigrating(true);
    try {
      const token = localStorage.getItem('ludovic_admin_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/migrate-photos-to-cloudinary`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.ok) {
        toast({
          title: `✅ Migration terminée`,
          description: `${data.migrated} photo(s) restaurées · ${data.not_found} non trouvées · ${data.errors} erreur(s)`
        });
        refresh();
      } else {
        throw new Error(data.detail || "Échec");
      }
    } catch (err) {
      toast({ title: "Erreur migration", description: err.message || "Échec", variant: "destructive" });
    }
    setMigrating(false);
  };

  const allCategories = [...GALLERY_CATEGORIES, ...customCats.map(c => ({ id: c.id, name: c.name, custom: true }))];

  const addCategory = async () => {
    const name = newCatName.trim();
    if (!name) return;
    setCreatingCat(true);
    try {
      const newCat = await createGalleryCategory(name);
      setNewCatName("");
      refresh();
      setCategory(newCat.id);
      toast({ title: "Catégorie créée ✅", description: newCat.name });
    } catch (err) {
      toast({ title: "Erreur", description: err.response?.data?.detail || "Impossible de créer", variant: "destructive" });
    }
    setCreatingCat(false);
  };

  const delCategory = async (catId, catName) => {
    const count = photos.filter(p => p.category === catId).length;
    const msg = count > 0
      ? `Supprimer la catégorie "${catName}" ET ses ${count} photo(s) ?`
      : `Supprimer la catégorie "${catName}" ?`;
    if (!window.confirm(msg)) return;
    try {
      await deleteGalleryCategory(catId);
      refresh();
      if (category === catId) setCategory("mariage");
      if (filter === catId) setFilter("all");
      toast({ title: "Catégorie supprimée" });
    } catch (err) {
      toast({ title: "Erreur", description: err.response?.data?.detail || "Échec", variant: "destructive" });
    }
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    let added = 0;
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      try {
        await uploadPhoto(file, category);
        added++;
      } catch (err) {
        toast({ title: "Erreur d'upload", description: err.response?.data?.detail || err.message, variant: "destructive" });
        break;
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    refresh();
    if (added > 0) toast({ title: `${added} photo${added > 1 ? 's' : ''} ajoutée${added > 1 ? 's' : ''}`, description: `Catégorie : ${allCategories.find(c => c.id === category)?.name}` });
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    await deletePhoto(id);
    refresh();
    toast({ title: "Photo supprimée" });
  };

  const filteredPhotos = filter === "all" ? photos : photos.filter(p => p.category === filter);

  return (
    <div className="space-y-6">
      {/* Restore legacy photos from Cloudinary */}
      <div className="border border-gold/30 bg-zinc-950/40 p-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-gold text-sm uppercase tracking-wider mb-1">Restaurer les photos depuis Cloudinary</h3>
          <p className="text-gray-400 text-xs">À utiliser après un redéploiement si des photos ne s'affichent plus. Rattache les URLs aux fichiers déjà sur Cloudinary. Sans risque.</p>
        </div>
        <button
          onClick={runMigration}
          disabled={migrating}
          data-testid="migrate-photos-btn"
          className="px-5 py-2.5 border border-gold text-gold hover:bg-gold hover:text-black transition-colors text-sm uppercase tracking-wider disabled:opacity-50 whitespace-nowrap"
        >
          {migrating ? "Restauration..." : "Restaurer"}
        </button>
      </div>

      {/* Custom categories management */}
      <div className="border border-gold/30 bg-zinc-950/40 p-6">
        <h3 className="text-xl mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          Ajouter une nouvelle catégorie
        </h3>
        <div className="flex gap-3 items-stretch">
          <input
            type="text"
            value={newCatName}
            onChange={(e) => setNewCatName(e.target.value)}
            placeholder="Ex: Artistique, Boudoir, Sport..."
            data-testid="new-category-input"
            className="flex-1 bg-black border border-white/20 focus:border-gold focus:outline-none p-3 text-white"
            onKeyDown={(e) => e.key === 'Enter' && addCategory()}
          />
          <button
            onClick={addCategory}
            disabled={creatingCat || !newCatName.trim()}
            data-testid="create-category-btn"
            className="btn-gold-filled px-5 disabled:opacity-50"
          >
            {creatingCat ? "..." : "Créer"}
          </button>
        </div>
        {customCats.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {customCats.map(c => (
              <span key={c.id} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-wider border border-gold/30 text-gold">
                {c.name}
                <button onClick={() => delCategory(c.id, c.name)} data-testid={`del-category-${c.id}`} className="text-red-400 hover:text-red-300">
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="border border-gold/30 bg-zinc-950/40 p-6">
        <h3 className="text-xl mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          Ajouter des photos à une galerie
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <div>
            <label className="block text-xs tracking-wider uppercase text-gray-400 mb-2">Catégorie</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="photo-category-select"
              className="w-full bg-black border border-white/20 focus:border-gold focus:outline-none p-3 text-white"
            >
              {allCategories.map(c => (
                <option key={c.id} value={c.id}>{c.name}{c.custom ? " (perso)" : ""}</option>
              ))}
            </select>
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFiles}
              className="hidden"
              id="photo-upload"
            />
            <label
              htmlFor="photo-upload"
              data-testid="photo-upload-btn"
              className={`btn-gold-filled w-full justify-center cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
            >
              <Upload size={16} />
              {uploading ? "Upload en cours..." : "Choisir des photos"}
            </label>
          </div>
        </div>
        <p className="text-gray-500 text-xs mt-3">
          Formats : JPG, PNG, WEBP. Redimensionnées automatiquement (max 1600px).
        </p>
      </div>

      <div>
        <p className="text-xs tracking-wider uppercase text-gray-400 mb-2">Filtrer</p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
              filter === "all" ? "border-gold bg-gold text-black" : "border-white/20 text-white/70 hover:border-gold"
            }`}
          >
            Tout ({photos.length})
          </button>
          {allCategories.map(c => {
            const count = photos.filter(p => p.category === c.id).length;
            if (count === 0) return null;
            return (
              <button
                key={c.id}
                onClick={() => setFilter(c.id)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
                  filter === c.id ? "border-gold bg-gold text-black" : "border-white/20 text-white/70 hover:border-gold"
                }`}
              >
                {c.name} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {filteredPhotos.length === 0 ? (
        <div className="border border-white/10 bg-zinc-950/40 p-10 text-center">
          <ImageIcon size={40} className="text-gold mx-auto mb-3" strokeWidth={1} />
          <p className="text-gray-400">Aucune photo uploadée pour le moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {filteredPhotos.map(p => {
            const cat = allCategories.find(c => c.id === p.category);
            return (
              <div key={p.id} className="relative group aspect-[3/4] border border-white/10 overflow-hidden">
                <img src={photoUrl(p.url)} alt={p.originalName} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-3">
                  <p className="text-gold text-xs uppercase tracking-wider">{cat?.name}</p>
                  <button
                    onClick={() => del(p.id)}
                    className="mt-2 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs uppercase tracking-wider flex items-center gap-1"
                  >
                    <Trash2 size={12} />
                    Supprimer
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const refresh = () => listMessages().then(setMessages).finally(() => setLoading(false));
  useEffect(() => { refresh(); }, []);

  const del = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    await deleteMessage(id);
    refresh();
    toast({ title: "Supprimé" });
  };

  if (loading) return <div className="p-10 text-center text-gray-400">Chargement...</div>;
  if (messages.length === 0) {
    return <div className="border border-white/10 bg-zinc-950/40 p-10 text-center"><p className="text-gray-400">Aucun message reçu.</p></div>;
  }

  return (
    <div className="space-y-4">
      {messages.map(m => (
        <div key={m.id} className="border border-white/10 bg-zinc-950/40 p-5">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <p className="text-xl mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                {m.subject || "(Sans sujet)"}
              </p>
              <p className="text-gray-400 text-sm">
                {m.name} • <a href={`mailto:${m.email}`} className="hover:text-gold">{m.email}</a>
                {m.phone && <> • <a href={`tel:${m.phone}`} className="hover:text-gold">{m.phone}</a></>}
              </p>
              <p className="text-gray-300 text-sm mt-3 whitespace-pre-wrap">{m.message}</p>
              <p className="text-gray-500 text-xs mt-2">{new Date(m.createdAt).toLocaleString('fr-FR')}</p>
            </div>
            <button onClick={() => del(m.id)} className="p-2 border border-white/20 hover:bg-white/10 text-gray-400">
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
